import React, { useState, useEffect, useMemo, useRef, useCallback, memo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useReports, useReportEntry, useReportHtml, useReportPdfDownload } from '../hooks/useReports';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../shared/components/EntityListPanel';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import MasterDetailLayout from '../../../shared/components/MasterDetailLayout';
import { useViewport } from '../../../shared/hooks/useViewport';
import { opsItemKind } from '../../event-management/utils/opsitemDisplay';
import type { ActionLogEntry } from '../../../types/frontend';

const reportListConfig: EntityListItemConfig<ActionLogEntry> = {
  getId: (e) => e.opsitem_id,
  getPrimaryText: (e) => e.title || 'Report',
  getSecondaryText: (e) => {
    const date = e.created_at ? new Date(e.created_at).toLocaleString() : '';
    return [e.rule_name, date].filter(Boolean).join(' · ');
  },
  getSearchText: (e) => `${e.title || ''} ${e.rule_name || ''}`,
  Icon: AssessmentIcon,
};

interface ReportsModalProps {
  customerId: string | undefined;
  isOpen: boolean;
  /** OpsItem id from a `?report=oi-...` deep link — selected once, when resolved. */
  initialOpsItemId?: string | null;
  onClose: () => void;
}

const ReportsModal: React.FC<ReportsModalProps> = ({ customerId, isOpen, initialOpsItemId, onClose }) => {
  const { isCompact } = useViewport();
  const { entries, loading, refresh } = useReports(isOpen ? customerId : undefined);
  // This modal outlives a customer switch — UserMenu keeps it mounted and only
  // toggles `isOpen` — so the selection carries the customer it was made under and
  // is dropped in the same render the customer changes. Clearing it in an effect
  // would be one render too late: the viewer would already have issued a report
  // read pairing the new customer with the previous customer's OpsItem id.
  const [selection, setSelection] = useState<{ customerId: string | undefined; entry: ActionLogEntry } | null>(null);
  const selectedEntry = selection && selection.customerId === customerId ? selection.entry : null;
  const selectEntry = useCallback(
    (entry: ActionLogEntry) => setSelection({ customerId, entry }),
    [customerId],
  );

  // Deep link: resolve the entry by id and select it once. Guarded by a ref so a
  // later manual selection is not yanked back to the linked report.
  const { entry: resolvedEntry, error: deepLinkError } = useReportEntry(isOpen ? customerId : undefined, initialOpsItemId);
  // `?report=` names a report, but GetActionLogItem resolves any action-log item by
  // id — an alert or automation id in the param must not be dressed up as a report.
  const deepLinkEntry = resolvedEntry && opsItemKind(resolvedEntry) === 'report' ? resolvedEntry : undefined;
  const linkedItemIsNotAReport = !!resolvedEntry && !deepLinkEntry;
  // Below `md` the notice that a link failed takes the screen, so leaving it with
  // back has to stick for that link; otherwise back would never reach the list.
  const [dismissedLink, setDismissedLink] = useState<string | null>(null);
  const linkFailed = (!!deepLinkError || linkedItemIsNotAReport) && dismissedLink !== initialOpsItemId;
  const selectedDeepLink = useRef<string | null>(null);
  // Forget the handled link once the param is gone, so the same link arriving again
  // opens its report instead of being read as already dealt with.
  useEffect(() => {
    if (!initialOpsItemId) {
      selectedDeepLink.current = null;
      setDismissedLink(null);
    }
  }, [initialOpsItemId]);
  const backToList = () => {
    setSelection(null);
    setDismissedLink(initialOpsItemId ?? null);
  };
  useEffect(() => {
    if (!deepLinkEntry) return;
    // Keyed by customer too, so the same link under a different customer resolves
    // and selects again rather than being treated as already handled.
    const key = `${customerId}:${deepLinkEntry.opsitem_id}`;
    if (selectedDeepLink.current === key) return;
    selectedDeepLink.current = key;
    setSelection({ customerId, entry: deepLinkEntry });
  }, [deepLinkEntry, customerId]);

  // The list is capped at 50, so a deep-linked older report may not be in it.
  // Show it anyway rather than leaving a selection with no row.
  const listEntries = useMemo(() => (
    deepLinkEntry && !entries.some((e) => e.opsitem_id === deepLinkEntry.opsitem_id)
      ? [deepLinkEntry, ...entries]
      : entries
  ), [deepLinkEntry, entries]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isCompact}
      PaperProps={{ sx: isCompact ? undefined : { width: '95vw', height: '95vh' } }}
    >
      <ModalHeader
        icon={AssessmentIcon}
        title="Reports"
        onClose={onClose}
        actions={
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        }
      />

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        <MasterDetailLayout
          hasSelection={!!selectedEntry || linkFailed}
          onBack={backToList}
          backLabel="Back to reports"
          list={
            <EntityListPanel
              entities={listEntries}
              selectedEntity={selectedEntry}
              isNew={false}
              saving={false}
              config={reportListConfig}
              emptyStateText="No reports yet"
              emptyStateHelperText="Reports appear here when a scheduled report rule generates output."
              onSelect={selectEntry}
              isLoading={loading}
              searchable
            />
          }
          detail={selectedEntry ? (
            // Keyed by report: the download is an async mutation living in this
            // component, and without a remount its pending and error state would
            // follow the reader to the next report they open — showing that one as
            // busy, and delivering the previous report's file while they look at it.
            <ReportViewer key={selectedEntry.opsitem_id} customerId={customerId} entry={selectedEntry} />
          ) : linkFailed ? (
            // The reader followed a link to one specific report. Say why it did not
            // open instead of showing the list as if they had opened Reports by hand.
            <EmptyStateMessage
              icon={AssessmentIcon}
              title="Linked report unavailable"
              description={deepLinkError
                ? `${deepLinkError.message}. Pick a report from the list instead.`
                : 'That link does not point at a report. Pick a report from the list instead.'}
            />
          ) : (
            <EmptyStateMessage
              icon={AssessmentIcon}
              title="Select a report"
              description="Choose a report from the list to view it"
            />
          )}
        />
      </DialogContent>

      {/* Counts `entries`, not `listEntries`: a deep-linked report older than the
          capped list is prepended as a row, but it is not an extra report, and
          counting it would report 51 of a list the server capped at 50. */}
      <ModalFooter
        count={entries.length}
        label={entries.length === 1 ? 'report' : 'reports'}
        onClose={onClose}
      />
    </Dialog>
  );
};

const ReportViewer: React.FC<{ customerId: string | undefined; entry: ActionLogEntry }> = ({ customerId, entry }) => {
  const { data: html, isLoading: loading, error: queryError } = useReportHtml(customerId, entry.opsitem_id);
  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to load report') : null;
  // The PDF is rendered and stored server-side, so downloading is a fetch of a
  // presigned URL rather than a print of the page. The first download of an older
  // report renders it, which is why this can take a moment and shows progress.
  const download = useReportPdfDownload(customerId);
  // `disabled` only blocks a click after React commits the pending state, so a
  // fast double-click can dispatch twice before that and start two server-side
  // renders. This flips synchronously, in the handler itself.
  const inFlight = useRef(false);
  const downloadPdf = useCallback(() => {
    if (inFlight.current) return;
    inFlight.current = true;
    download.mutate(entry.opsitem_id, {
      onSettled: () => {
        inFlight.current = false;
      },
      onSuccess: ({ url, filename }) => {
        // An anchor click rather than a location assignment: the object is served
        // with Content-Disposition: attachment, so the browser saves it and the
        // app stays where it is instead of navigating away from the report.
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.rel = 'noopener';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
    });
  }, [download, entry.opsitem_id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !html) {
    return (
      <EmptyStateMessage
        icon={AssessmentIcon}
        title="Report unavailable"
        description={error || 'This report has no rendered content.'}
      />
    );
  }

  // Sandboxed iframe: the report is self-contained branded HTML. No allow-scripts,
  // so embedded SVG/HTML cannot script the host and report styles stay isolated.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" noWrap sx={{ minWidth: 0 }}>
          {entry.title || 'Report'}
        </Typography>
        {download.isError && (
          // Say the download failed rather than leaving a button that did nothing.
          <Typography variant="caption" color="error" sx={{ ml: 'auto', mr: 1 }}>
            {download.error instanceof Error ? download.error.message : 'Could not prepare the PDF'}
          </Typography>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={download.isPending ? <CircularProgress size={16} /> : <DownloadIcon />}
          disabled={download.isPending}
          onClick={downloadPdf}
          // The title beside it truncates instead, so the label stays on one line inside its outline.
          sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {download.isPending ? 'Preparing PDF' : 'Download PDF'}
        </Button>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <iframe
          title={entry.title || 'Report'}
          srcDoc={html}
          sandbox=""
          style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
        />
      </Box>
    </Box>
  );
};

export default memo(ReportsModal);
